import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface CreateNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'goal' | 'habit' | 'task';
  entityId: string;
  entityTitle: string;
}

export default function CreateNoteModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityTitle,
}: CreateNoteModalProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [linkToEntity, setLinkToEntity] = useState(true);

  const createNoteMutation = useMutation({
    mutationFn: async () => {
      // Create the note
      const noteResponse = await apiRequest("POST", "/api/notes", {
        title,
        content: { text: content },
        plainContent: content,
        tags,
      });
      const noteData = await noteResponse.json();

      // If checkbox is checked, create the link
      if (linkToEntity) {
        await apiRequest("POST", `/api/notes/${noteData.id}/links`, {
          entityType,
          entityId,
        });
      }

      return noteData;
    },
    onSuccess: () => {
      // Invalidate both notes and entity notes caches
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api", entityType, entityId, "notes"] });
      
      // Reset form and close modal
      handleClose();
    },
    onError: () => {
    },
  });

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!createNoteMutation.isPending) submitLockRef.current = false;
  }, [createNoteMutation.isPending]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      return;
    }
    if (submitLockRef.current || createNoteMutation.isPending) return;
    submitLockRef.current = true;
    createNoteMutation.mutate();
  };

  const handleClose = () => {
    setTitle("");
    setContent("");
    setTags([]);
    setTagInput("");
    setLinkToEntity(true);
    onClose();
  };

  const entityTypeLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Note</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="note-title">Title</Label>
            <Input
              id="note-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter note title..."
              data-testid="input-quick-note-title"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="note-tags">Tags</Label>
            <div className="flex gap-2">
              <Input
                id="note-tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add tags (press Enter)"
                data-testid="input-quick-note-tags"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddTag}
                disabled={!tagInput.trim()}
              >
                Add
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="note-content">Content</Label>
            <Textarea
              id="note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your note..."
              rows={8}
              className="resize-none"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="link-to-entity"
              checked={linkToEntity}
              onCheckedChange={(checked) => setLinkToEntity(checked as boolean)}
              data-testid="checkbox-link-to-entity"
            />
            <Label
              htmlFor="link-to-entity"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Link to {entityTypeLabel}: {entityTitle}
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createNoteMutation.isPending}
              data-testid="button-cancel-quick-note"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitLockRef.current || createNoteMutation.isPending || !title.trim()}
              data-testid="button-create-quick-note"
            >
              {createNoteMutation.isPending ? "Creating..." : "Create Note"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
